import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { cn } from '../ui/cn';
import Button from '../ui/Button';
import MegaMenu, { type MegaGroup } from './MegaMenu';
import MobileMenu from './MobileMenu';
import { ChevronIcon } from './Icons';
import { useHeader } from './headerState';
import { useNavData } from './useNavData';
import { mainLinks } from './siteInfo';
import { useRecentWorks } from './useRecentWorks';
import { capitalize, formatLocation } from '../../utils/text';
import type { Service } from '../../types/api';

type MenuId = 'sectors' | 'services';

export default function Header() {
  const { overHero } = useHeader();
  const { domains, sectors, services } = useNavData();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState<MenuId | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(null); setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const sectorGroups: MegaGroup[] = useMemo(
    () => domains.flatMap((d) => sectors.filter((s) => s.domain_id === d.id).map((s) => ({
      id: s.id,
      label: s.name,
      heading: d.name,
      description: s.description,
      cta: { label: 'Voir le secteur', to: `/secteurs/${s.id}` },
      links: services.filter((sv) => sv.sector_id === s.id).map((sv) => ({ label: sv.name, to: `/services/${sv.id}` })),
    }))),
    [domains, sectors, services],
  );
  // Menu Services: jusqu'à 6 services par secteur (le secteur en petit titre); au survol d'un service,
  // sa description, un bouton « Voir le service » et ses 5 dernières réalisations
  const [servicesSeen, setServicesSeen] = useState(false);
  const { works, error: worksError, retry: retryWorks } = useRecentWorks(servicesSeen);
  const serviceGroups: MegaGroup[] = useMemo(() => {
    const bySector = new Map<string, Service[]>();
    for (const sv of services) bySector.set(sv.sector_id, [...(bySector.get(sv.sector_id) ?? []), sv]);
    return [...bySector.values()].flatMap((list) => list.slice(0, 6)).slice(0, 9).map((sv) => ({
      id: sv.id,
      label: sv.name,
      heading: sv.sector?.name,
      description: sv.description,
      cta: { label: 'Voir le service', to: `/services/${sv.id}` },
      links: [],
      projects: worksError
        ? null
        : works
        ? works.filter((w) => w.service_id === sv.id).slice(0, 5).map((w) => ({ id: w.id, title: capitalize(w.title), location: formatLocation(w.location), to: `/works/${w.id}` }))
        : undefined,
    }));
  }, [services, works, worksError]);

  const transparent = overHero && !scrolled && !open && !mobileOpen;
  const show = (id: MenuId) => { window.clearTimeout(closeTimer.current); setOpen(id); if (id === 'services') setServicesSeen(true); };
  const hideSoon = () => { window.clearTimeout(closeTimer.current); closeTimer.current = window.setTimeout(() => setOpen(null), 160); };

  const link = 'relative flex h-[5.5rem] items-center px-4 text-[0.9375rem] font-medium text-inherit transition-colors';
  const underline = 'absolute inset-x-4 bottom-0 h-0.5 origin-left bg-brand transition-transform duration-300';

  const trigger = (id: MenuId, label: string) => (
    <li onMouseEnter={() => show(id)} onMouseLeave={hideSoon}>
      <button
        aria-expanded={open === id}
        aria-haspopup="true"
        onClick={() => setOpen(open === id ? null : id)}
        onFocus={() => show(id)}
        className={cn(link, 'gap-2', open === id && 'text-brand-dark')}
      >
        {label}
        <ChevronIcon className={cn('h-2 w-3 transition-transform duration-300', open === id && 'rotate-180')} />
        <span aria-hidden className={cn(underline, open === id ? 'scale-x-100' : 'scale-x-0')} />
      </button>
    </li>
  );

  return (
    <>
      <div
        role="banner"
        onMouseLeave={hideSoon}
        className={cn(
          'sticky top-0 z-50 h-[5.5rem] transition-[background-color,box-shadow,color] duration-300',
          transparent ? 'bg-transparent text-white' : 'bg-white text-black',
          !transparent && scrolled && !open && 'shadow-[0_1px_0_var(--color-line)]',
        )}
      >
        <div className="site-container flex h-full items-center justify-between">
          <Link to="/" aria-label="Flex Group, accueil" className="shrink-0">
            <img src="/logo.png" alt="" className={cn('h-12 w-auto transition-[filter] duration-300', transparent && 'brightness-0 invert')} />
          </Link>

          <nav aria-label="Navigation principale" className="hidden items-center lg:flex">
            <ul className="flex items-center" onMouseEnter={() => window.clearTimeout(closeTimer.current)}>
              {trigger('sectors', 'Secteurs')}
              {trigger('services', 'Services')}
              {mainLinks.map((l) => (
                <li key={l.to} onMouseEnter={() => setOpen(null)}>
                  <NavLink to={l.to} className={({ isActive }) => cn(link, 'group', isActive && !transparent && '!text-brand-dark')}>
                    {({ isActive }) => (
                      <>
                        {l.label}
                        <span aria-hidden className={cn(underline, isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100')} />
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="ml-6" onMouseEnter={() => setOpen(null)}>
              <Button to="/contact" className="h-11 px-6">Contact</Button>
            </div>
          </nav>

          <button
            className="flex h-11 w-11 flex-col items-center justify-center gap-1.5 lg:hidden"
            aria-label="Ouvrir le menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
          >
            <span className="h-0.5 w-6 bg-current" /><span className="h-0.5 w-6 bg-current" /><span className="h-0.5 w-6 bg-current" />
          </button>
        </div>

        <div onMouseEnter={() => window.clearTimeout(closeTimer.current)} className="hidden lg:block">
          {open === 'sectors' && <MegaMenu title="Secteurs" groups={sectorGroups} onNavigate={() => setOpen(null)} />}
          {open === 'services' && <MegaMenu title="Services" groups={serviceGroups} footer={{ label: 'Voir tous les services', to: '/services' }} onRetryProjects={retryWorks} onNavigate={() => setOpen(null)} />}
        </div>
      </div>

      {open && <div aria-hidden className="fixed inset-0 z-40 hidden bg-black/30 lg:block" onMouseEnter={() => setOpen(null)} />}
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} sectorGroups={sectorGroups} serviceGroups={serviceGroups} />
    </>
  );
}
